/**
 * Encounter-level meta — name, grid mode, grid extent, environment, and the sitrep.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, isGridMode, ok } from "@lancer-vtt/shared";
import { MAX_GRID_DIM, MIN_GRID_DIM, encounterEnvironment, encounterGridMode, gridExtent, isEncounterEnvironment, isEncounterGround, isSitrepType, type SitrepProgress } from "../types";
import { changed, type EncounterHandler } from "./context";

export const applyRenameEncounter: EncounterHandler<"rename_encounter"> = (state, cmd) => {
  const name = cmd.name.trim();
  if (name === "") return err(new CommandError("rename_encounter: name must not be empty"));
  const prev = state.name;
  return ok({
    state: { ...state, name },
    inverse: { type: "rename_encounter", actorId: cmd.actorId, name: prev },
    events: changed(null),
  });
};

export const applySetGridMode: EncounterHandler<"set_grid_mode"> = (state, cmd) => {
  // Reject a non-mode (undo round-trips any known mode); the "v1 = 2d-square only"
  // policy lives at the create endpoint + the editor's picker, not here.
  if (!isGridMode(cmd.gridMode)) {
    return err(new CommandError(`set_grid_mode: unknown grid mode ${JSON.stringify(cmd.gridMode)}`));
  }
  // Empty-map guard: the grid is a setup-time choice, fixed at creation.
  // Switching grids is only legal on an EMPTY map — no placed
  // combatant, no terrain, no edges — so a position in one grid's coordinates can
  // never strand in an encounter whose grid is now the other. `restore`/the inverse
  // is NOT gated (it only replays a state the encounter was already in). A no-op
  // (same mode) is always allowed.
  if (cmd.gridMode !== encounterGridMode(state)) {
    const placed = Object.values(state.combatants).find((c) => c.pos != null);
    if (placed) {
      return err(
        new CommandError(
          `set_grid_mode: cannot change grid mode while a combatant is placed (${placed.id}); ` +
            `the grid is fixed once the map has content`,
        ),
      );
    }
    if (Object.keys(state.terrain).length > 0 || Object.keys(state.edges).length > 0) {
      return err(
        new CommandError(
          "set_grid_mode: cannot change grid mode while terrain or edges exist; clear the map first",
        ),
      );
    }
  }
  const prev = encounterGridMode(state);
  return ok({
    state: { ...state, gridMode: cmd.gridMode },
    inverse: { type: "set_grid_mode", actorId: cmd.actorId, gridMode: prev },
    events: changed(null),
  });
};

export const applyResizeGrid: EncounterHandler<"resize_grid"> = (state, cmd) => {
  // Each dimension is an integer in [MIN_GRID_DIM, MAX_GRID_DIM] — the reducer is the
  // wall (a driver can't author a 0-wide or runaway board). This sets the SQUARE extent
  // only; `mapRadius` (the hex-disk bound) is deliberately untouched — the two coordinate
  // systems size independently until the square placement system subsumes the hex one.
  for (const [dim, v] of [["width", cmd.width], ["height", cmd.height]] as const) {
    if (!Number.isInteger(v) || v < MIN_GRID_DIM || v > MAX_GRID_DIM) {
      return err(
        new CommandError(
          `resize_grid: ${dim} must be an integer in [${MIN_GRID_DIM}, ${MAX_GRID_DIM}] (got ${v})`,
        ),
      );
    }
  }
  // Capture the prior *effective* extent (through gridExtent) so undoing the first-ever
  // resize restores the inscribed-disk default, not `undefined`.
  const prevExtent = gridExtent(state);
  return ok({
    state: { ...state, gridWidth: cmd.width, gridHeight: cmd.height },
    inverse: {
      type: "resize_grid",
      actorId: cmd.actorId,
      width: prevExtent.width,
      height: prevExtent.height,
    },
    events: changed(null),
  });
};

export const applySetEnvironment: EncounterHandler<"set_environment"> = (state, cmd) => {
  // `null` clears back to "no special environment" (`environment: undefined` — the
  // additive-optional convention every other EncounterState field this shape uses);
  // a non-null value must be a known EncounterEnvironment (undo round-trips either way).
  if (cmd.environment !== null && !isEncounterEnvironment(cmd.environment)) {
    return err(
      new CommandError(`set_environment: unknown environment ${JSON.stringify(cmd.environment)}`),
    );
  }
  const prev = encounterEnvironment(state);
  return ok({
    state: { ...state, environment: cmd.environment ?? undefined },
    inverse: { type: "set_environment", actorId: cmd.actorId, environment: prev },
    events: changed(null),
  });
};

export const applySetGround: EncounterHandler<"set_ground"> = (state, cmd) => {
  // `null` clears back to the default ground (`ground: undefined`, the additive-optional
  // convention `set_environment` uses); a non-null value must be a known ground.
  if (cmd.ground !== null && !isEncounterGround(cmd.ground)) {
    return err(new CommandError(`set_ground: unknown ground ${JSON.stringify(cmd.ground)}`));
  }
  return ok({
    state: { ...state, ground: cmd.ground ?? undefined },
    inverse: { type: "set_ground", actorId: cmd.actorId, ground: state.ground ?? null },
    events: changed(null),
  });
};

export const applySetSitrep: EncounterHandler<"set_sitrep"> = (state, cmd) => {
  // Author (or clear) the whole sitrep in bulk. `null`
  // clears back to deathmatch — mirrors `set_environment`'s `T | null` clear-payload
  // convention. The inverse is the SAME command type carrying the prior `sitrep` (or
  // `null`); replaying it re-derives `sitrepProgress` via the same seeding rule below —
  // see `SetSitrepCommand`'s docstring for the reseed-on-reauthor caveat that puts on
  // any other `sitrepProgress` mutator.
  const prevSitrep = state.sitrep ?? null;
  if (cmd.sitrep === null) {
    return ok({
      state: { ...state, sitrep: undefined, sitrepProgress: undefined },
      inverse: { type: "set_sitrep", actorId: cmd.actorId, sitrep: prevSitrep },
      events: changed(null),
    });
  }
  if (!isSitrepType(cmd.sitrep.type)) {
    return err(new CommandError(`set_sitrep: unknown sitrep type ${JSON.stringify(cmd.sitrep.type)}`));
  }
  if (!Number.isInteger(cmd.sitrep.roundLimit) || cmd.sitrep.roundLimit < 1) {
    return err(
      new CommandError(`set_sitrep: roundLimit must be a positive integer (got ${cmd.sitrep.roundLimit})`),
    );
  }
  const sitrep = cmd.sitrep;
  // Only Control accumulates a per-round score — every other type reads
  // round/positions directly with no stored progress.
  const progress: SitrepProgress | undefined =
    sitrep.type === "control" ? { scores: { pc: 0, npc: 0 } } : undefined;
  return ok({
    state: { ...state, sitrep, sitrepProgress: progress },
    inverse: { type: "set_sitrep", actorId: cmd.actorId, sitrep: prevSitrep },
    events: changed(null),
  });
};

export const applyEndCombat: EncounterHandler<"end_combat"> = (state, cmd) => {
  if (state.combatEndedAt !== undefined) {
    return err(new CommandError(`end_combat: the fight already ended at ${state.combatEndedAt}`));
  }
  return ok({
    state: { ...state, combatEndedAt: cmd.at },
    inverse: { type: "reopen_combat", actorId: cmd.actorId },
    events: changed(null),
  });
};

export const applyReopenCombat: EncounterHandler<"reopen_combat"> = (state, cmd) => {
  const prev = state.combatEndedAt;
  if (prev === undefined) return err(new CommandError("reopen_combat: the fight is not over"));
  const { combatEndedAt: _ended, ...live } = state;
  return ok({
    state: live,
    inverse: { type: "end_combat", actorId: cmd.actorId, at: prev },
    events: changed(null),
  });
};
